export const chineseHumanoidRobotsPage = {
  path: "/humanoid-robots/china",
  title: "Chinese Humanoid Robots China’s Fastest-Growing Humanoid Robotics Ecosystem",
  seoTitle: "Chinese Humanoid Robots Unitree, UBTECH, Fourier, AgiBot, XPeng, Leju and More",
  seoDescription:
    "Explore China’s humanoid robotics ecosystem, from Unitree G1 and UBTECH Walker S to Fourier GR-2, AgiBot A2, XPeng IRON, Leju Kuavo, EngineAI, Astribot, Kepler and Xiaomi CyberOne.",
  description:
    "Chinese companies work on industrial humanoids, low-cost research platforms, factory pilots, service robots, embodied AI datasets and full-body manipulation.",
  heroText:
    "China’s humanoid robotics ecosystem is moving fast. Hangzhou has Unitree. Shenzhen has UBTECH, Leju and EngineAI. Shanghai has Fourier, AgiBot and Kepler. Guangzhou has XPeng IRON. Beijing is pushing public humanoid research platforms and national robotics programs. The important distinction is between a robot shown in a controlled demo, a robot tested in a pilot and a robot proven in daily industrial operation.",
  updatedAt: "October 11, 2026",
  author: "TechniaHQRobot Robotics Desk",
  readingTime: "18 min read",
  keywords: [
    "Chinese humanoid robots",
    "humanoid robots in China",
    "China robotics",
    "Unitree G1",
    "Unitree H1",
    "Unitree R1",
    "UBTECH Walker S",
    "UBTECH Walker S2",
    "Fourier GR-1",
    "Fourier GR-2",
    "AgiBot A2",
    "AgiBot humanoid robot",
    "XPeng IRON",
    "Leju Kuavo",
    "EngineAI SE01",
    "Astribot S1",
    "Kepler humanoid robot",
    "Xiaomi CyberOne",
    "CloudMinds XR-1",
    "embodied AI China",
    "physical AI China",
    "China humanoid robot companies"
  ],
  summaryCards: [
    {
      "label": "Low-cost humanoid platforms",
      "value": "3+",
      "detail": "Unitree G1, Unitree R1 and EngineAI SA01 show the push toward lower-cost bodies for developers, labs and demonstrations."
    },
    {
      "label": "Industrial humanoids",
      "value": "6+",
      "detail": "UBTECH Walker S, AgiBot A2, Fourier GR-2, XPeng IRON, Leju Kuavo and EngineAI SE01 target factory, logistics or enterprise work in different stages."
    },
    {
      "label": "Embodied AI and manipulation",
      "value": "4",
      "detail": "AgiBot World, Astribot S1, Galbot G1 / G1 Lite and Xiaomi Robotics show the link between humanoid hardware and robot data pipelines."
    },
    {
      "label": "Service and companion robots",
      "value": "4",
      "detail": "UBTECH Walker X, UWorld U1, CloudMinds XR-1 and Xiaomi CyberOne sit closer to service, interaction or prototype companion robotics."
    },
    {
      "label": "Public research",
      "value": "2",
      "detail": "Tiangong and Xianxingzhe connect Beijing and Changsha to national humanoid research and bipedal robotics history."
    },
    {
      "label": "Wheeled manipulation platforms",
      "value": "1 family",
      "detail": "Galbot G1 and G1 Lite have developer documentation. Availability differs between the hardware and individual task packages."
    }
  ],
  robots: [
    {
      id: "unitree-h1",
      name: "Unitree H1 / H1-2",
      company: "Unitree Robotics",
      city: "Hangzhou",
      status: "Active platform",
      statusTone: "active",
      year: "2023",
      mainUse: "Full-size humanoid research, demonstrations and locomotion development.",
      mobility: "Bipedal full-size humanoid",
      keyHardware: "3D LiDAR plus depth camera, high-torque joints and quick-replace battery on H1 according to Unitree; H1-2 adds more arm and body capability.",
      autonomy: "Controlled demos, developer operation and research use. Do not treat public locomotion videos as proof of daily factory autonomy.",
      sourceLabel: "Unitree H1 official",
      sourceUrl: "https://www.unitree.com/h1/",
      overview:
        "H1 is Unitree’s larger humanoid line and one of the robots that made Hangzhou visible in the global humanoid race. It is built as a full-size biped platform rather than a small desktop research toy.",
      technicalNotes:
        "Unitree lists H1 around 180 cm and about 47 kg, with 3D LiDAR plus depth camera perception and a quickly replaceable 864 Wh battery. H1-2 is listed separately with a heavier body and a higher degree-of-freedom configuration.",
      whyItMatters:
        "H1 matters because it gave Unitree a full-size humanoid body while the company already had experience manufacturing legged robots and compact actuators.",
      limitations:
        "Locomotion demos do not prove autonomous industrial work. The page keeps H1 as an active platform, not a confirmed general-purpose worker."
    },
    {
      id: "unitree-g1",
      name: "Unitree G1",
      company: "Unitree Robotics",
      city: "Hangzhou",
      status: "Active lower-cost platform",
      statusTone: "active",
      year: "2024",
      mainUse: "Research, development, education, demonstrations and embodied AI experiments.",
      mobility: "Compact bipedal humanoid",
      keyHardware: "23 to 43 joint motors depending on configuration, optional three-finger dexterous hand, industrial crossed roller bearings and Unitree’s robot model stack.",
      autonomy: "Developer platform and controlled task demonstrations. It is not presented here as a proven home worker.",
      sourceLabel: "Unitree G1 official",
      sourceUrl: "https://www.unitree.com/g1/",
      overview:
        "G1 changed the economics of humanoid robotics because it brought a compact bipedal humanoid body into a price band that universities, labs and developers can discuss seriously.",
      technicalNotes:
        "Unitree lists G1 at 1320 mm standing height and about 35 kg with battery. The base configuration has 23 joint degrees of freedom while the EDU configuration can expand to higher joint counts and optional force-control three-finger hands.",
      whyItMatters:
        "G1 gives researchers a body for imitation learning, teleoperation, fall recovery, manipulation and whole-body control without the cost profile of large industrial humanoids.",
      limitations:
        "A low sticker price does not include integration, spare parts, safety work, supervision and task engineering. It should be treated as a platform, not a finished worker."
    },
    {
      id: "unitree-r1",
      name: "Unitree R1",
      company: "Unitree Robotics",
      city: "Hangzhou",
      status: "R1 product family / configuration-specific development access",
      statusTone: "active",
      year: "Manufacturer product page checked October 2026",
      mainUse: "Compact humanoid platform; secondary development is listed for R1 EDU.",
      mobility: "Bipedal humanoid",
      keyHardware: "The manufacturer lists R1 at 123 cm, about 29 kg with battery and 26 joints. R1 AIR has 20 joints; R1 EDU lists 26 to 40.",
      autonomy: "Not publicly confirmed for independent industrial work in this article. Treated as a platform under active iteration.",
      sourceLabel: "Unitree R1 configuration table",
      sourceUrl: "https://www.unitree.com/R1/",
      overview:
        "Unitree publishes three R1 configurations with different joint counts and development access. A purchase comparison needs the exact AIR, R1 or EDU designation.",
      technicalNotes:
        "The official table lists secondary development for R1 EDU only. It lists about one hour of battery life, subject to configuration and operating conditions. These are manufacturer specifications checked on 11 October 2026.",
      whyItMatters:
        "The configuration changes both the available joints and whether the buyer can develop control software. Those differences affect a laboratory project before any task trial begins.",
      limitations:
        "The product sheet does not establish industrial task success or shift-long reliability. Unitree says some illustrated functions remain under development."},
    {
      id: "ubtech-walker-x",
      name: "UBTECH Walker X",
      company: "UBTECH Robotics",
      city: "Shenzhen",
      status: "Service humanoid",
      statusTone: "commercial",
      year: "Walker family, commercial/service line",
      mainUse: "Commercial service, exhibition, interaction and human-facing applications.",
      mobility: "Bipedal humanoid service robot",
      keyHardware: "Walker X is listed by UBTECH under commercial humanoid products. Keep detailed specs source-specific.",
      autonomy: "Service and interaction demos. Not presented as unsupervised public-space autonomy.",
      sourceLabel: "UBTECH Walker X official",
      sourceUrl: "https://www.ubtrobot.com/en/humanoid/products/walker-x",
      overview:
        "Walker X represents UBTECH’s service humanoid branch. It is separate from Walker S industrial robots and should not be treated as the same deployment category.",
      technicalNotes:
        "The robot sits in UBTECH’s commercial humanoid products, where the focus is user interaction, service settings and public-facing demonstrations rather than heavy factory handling.",
      whyItMatters:
        "Walker X shows how UBTECH built a humanoid brand before the more industrial Walker S line became central to its factory narrative.",
      limitations:
        "Public service demos are not enough to claim unsupervised operation in hospitals, airports or malls."
    },
    {
      id: "ubtech-walker-s-series",
      name: "UBTECH Walker S / S1 / S2",
      company: "UBTECH Robotics",
      city: "Shenzhen",
      status: "Industrial pilot / commercial series",
      statusTone: "pilot",
      year: "2024 onward; S2 in 2025 / 2026 public material",
      mainUse: "Industrial manufacturing, logistics, material handling and factory pilot tasks.",
      mobility: "Bipedal industrial humanoid",
      keyHardware: "Walker S2 includes autonomous battery swapping, dual-battery power management, binocular stereo vision and 15 kg payload claims on UBTECH’s product page.",
      autonomy: "Industrial pilot and task-specific automation. Airbus described the aerospace collaboration as early testing, so this is not written as mass replacement.",
      sourceLabel: "UBTECH Walker S2 official",
      sourceUrl: "https://www.ubtrobot.com/en/humanoid/products/walker-s2",
      overview:
        "Walker S is one of the most important Chinese industrial humanoid lines because UBTECH is pushing it into factories, logistics workflows and aerospace testing partnerships.",
      technicalNotes:
        "UBTECH describes Walker S2 as an industrial humanoid with battery swapping within three minutes, power management, ground-reaching movement and binocular stereo vision. S1 is positioned for multi-task industrial scenarios.",
      whyItMatters:
        "Walker S matters because it forces a harder question than stage demos can a humanoid work repeatedly in manufacturing cells without blocking humans or production lines?",
      limitations:
        "Factory pilots and purchase agreements remain different from proven daily deployment at scale. The article labels the series as pilot/commercial, not fully autonomous factory labor."
    },
    {
      id: "ubtech-uworld-u1",
      name: "UBTECH UWorld U1",
      company: "UBTECH Robotics",
      city: "Shenzhen",
      status: "Companion humanoid / watch carefully",
      statusTone: "watchlist",
      year: "2026 launch coverage",
      mainUse: "Lifelike companion robot series and emotional interaction concept.",
      mobility: "Android-style humanoid / companion form factor",
      keyHardware: "Public coverage describes expressive faces, silicone skin and emotion-aware interaction, but this page avoids detailed specs unless UBTECH publishes stable product sheets.",
      autonomy: "Interaction-oriented AI claims, not independent household work.",
      sourceLabel: "Reuters UWorld U1 coverage",
      sourceUrl: "https://www.reuters.com/technology/chinas-ubtech-launches-ai-powered-lifelike-companion-robots-2026-07-02/",
      overview:
        "UWorld U1 sits outside the industrial Walker S line. It belongs to the social, companion and lifelike humanoid branch of UBTECH’s work.",
      technicalNotes:
        "Because public discussion around U1 focuses heavily on emotional response, lifelike presentation and pricing, the article avoids treating it as a factory robot or a proven household assistant.",
      whyItMatters:
        "U1 shows that China’s humanoid ecosystem is not only about factories. It also includes social robotics, companionship and the hard ethics of lifelike machines.",
      limitations:
        "Companion claims need caution. Emotional interaction is not the same as practical household manipulation or independent care."
    },
    {
      id: "fourier-gr-1",
      name: "Fourier GR-1",
      company: "Fourier",
      city: "Shanghai",
      status: "Active research / commercial platform",
      statusTone: "active",
      year: "2023 public launch visibility",
      mainUse: "General-purpose humanoid research platform and transition from rehabilitation robotics into humanoids.",
      mobility: "Bipedal humanoid",
      keyHardware: "Fourier lists the GRx humanoid series officially; public technical detail depends on model and configuration.",
      autonomy: "Developer and research platform; not presented as independent mass-deployed worker.",
      sourceLabel: "Fourier GRx official",
      sourceUrl: "https://www.fftai.com/",
      overview:
        "GR-1 is the robot that made Fourier’s move from rehabilitation robotics into humanoids visible. It connected Shanghai’s rehab robotics base to embodied AI and full-body platforms.",
      technicalNotes:
        "Fourier’s official site groups GR-1, GR-2 and GR-3 under its GRx humanoid robot series. The page avoids model-specific numbers unless the official product source is clear.",
      whyItMatters:
        "GR-1 matters because Fourier did not come from internet software. It came from medical and rehabilitation robotics, where actuation, safety and human contact already matter.",
      limitations:
        "Research deliveries and demos do not mean generalized industrial deployment. Treat GR-1 as a platform."
    },
    {
      id: "fourier-gr-2",
      name: "Fourier GR-2",
      company: "Fourier",
      city: "Shanghai",
      status: "Active humanoid platform",
      statusTone: "active",
      year: "2024 public launch visibility",
      mainUse: "Humanoid platform for research, service experiments and embodied AI development.",
      mobility: "Bipedal humanoid",
      keyHardware: "Official Fourier site lists GR-2 inside the GRx series; secondary reporting describes improved hardware and SDK over GR-1.",
      autonomy: "Developer and pilot-oriented. Not presented as fully autonomous.",
      sourceLabel: "Fourier GR-2 official",
      sourceUrl: "https://www.fftai.com/",
      overview:
        "GR-2 is presented as the upgraded middle generation in Fourier’s GRx series. It keeps the body format while shifting toward stronger developer access and more capable manipulation experiments.",
      technicalNotes:
        "Where official pages do not expose every parameter in crawlable text, this page keeps the hardware note short and points readers back to Fourier’s product pages.",
      whyItMatters:
        "GR-2 matters because China’s humanoid race is not only Shenzhen and Hangzhou. Shanghai has a cluster around Fourier, AgiBot and Kepler.",
      limitations:
        "Do not read marketing videos as evidence of high-duty industrial reliability."
    },
    {
      id: "fourier-gr-3",
      name: "Fourier GR-3",
      company: "Fourier",
      city: "Shanghai",
      status: "Recent platform / care-focused positioning",
      statusTone: "active",
      year: "2025 / 2026 public visibility",
      mainUse: "Care, companion and assistance-oriented humanoid positioning with research and institutional interest.",
      mobility: "Bipedal humanoid",
      keyHardware: "Fourier’s site shows GR-3 in its GRx humanoid series; public materials position it as a caring and capable companion.",
      autonomy: "Care and interaction positioning. Not written as independent eldercare replacement.",
      sourceLabel: "Fourier GR-3 official",
      sourceUrl: "https://www.fftai.com/",
      overview:
        "GR-3 moves Fourier’s humanoid story toward care, assistance and human-facing interaction while still sitting inside the GRx hardware family.",
      technicalNotes:
        "The current site headline frames GR-3 as a caring and capable companion. The article treats that as positioning, not proof of independent caregiving.",
      whyItMatters:
        "GR-3 shows how humanoid companies test different markets factory work, research platforms, care settings and service interaction.",
      limitations:
        "Eldercare requires reliability, safety, consent, cleaning, privacy and supervision. The page does not claim those problems are solved."
    },
    {
      id: "agibot-a2",
      name: "AgiBot A2 / A2 Max / A2-W",
      company: "AgiBot / Zhiyuan Robotics",
      city: "Shanghai",
      status: "Active humanoid platform",
      statusTone: "active",
      year: "2024 / 2025 public product line",
      mainUse: "Full-size humanoid platform, flexible manufacturing and embodied AI product family.",
      mobility: "Bipedal and wheeled variants depending on model",
      keyHardware: "AgiBot lists A2 Ultra, A2 Lite and A2-W on its official product navigation and links VR teleoperation and embodied AI data tools to the A2 ecosystem.",
      autonomy: "Controlled demos, teleoperation data collection and task-specific industrial work. Not presented as full autonomy.",
      sourceLabel: "AgiBot official products",
      sourceUrl: "https://www.agibot.com/",
      overview:
        "AgiBot A2 is one of the central names in Shanghai’s humanoid push. The company connects robots, data collection, teleoperation and embodied AI infrastructure.",
      technicalNotes:
        "AgiBot’s own product navigation separates A2 Ultra, A2 Lite and A2-W. A2-W is framed around flexible manufacturing, while A2 variants cover full-size humanoid use and performance positioning.",
      whyItMatters:
        "AgiBot matters because it is building the data side of humanoid robotics as aggressively as the hardware side.",
      limitations:
        "Public videos and Guinness-style endurance feats are not the same as verified factory productivity. The page separates demonstrations from deployment claims."
    },
    {
      id: "agibot-a3",
      name: "AgiBot A3",
      company: "AgiBot / Zhiyuan Robotics",
      city: "Shanghai",
      status: "Manufacturer-listed performance platform",
      statusTone: "active",
      year: "Manufacturer product page checked October 2026",
      mainUse: "Performance, interaction, entertainment, research and education as stated by AGIBOT.",
      mobility: "Bipedal humanoid",
      keyHardware: "AGIBOT lists A3 at 173 cm and 55 kg, with dual battery packs and maximum endurance of 10 hours.",
      autonomy: "The product page describes scripted motion tools, interaction and fleet coordination. Independent industrial task performance is not established here.",
      sourceLabel: "AGIBOT A3 product specification",
      sourceUrl: "https://www.agibot.com/products/A3",
      overview:
        "AGIBOT now publishes a dedicated A3 product page. It positions the bipedal platform for performances, interaction and research.",
      technicalNotes:
        "The page lists 173 cm height, 55 kg mass and up to 10 hours of endurance. The runtime is a manufacturer maximum without a matched task-duty cycle in this record. Specifications were checked on 11 October 2026.",
      whyItMatters:
        "The official application list helps separate stage motion and interaction from repeated material-handling work.",
      limitations:
        "A product specification and a coordinated performance do not establish factory uptime, handling success or operation without human assistance."},
    {
      id: "agibot-x1-x2",
      name: "AgiBot X1 / X2",
      company: "AgiBot / Zhiyuan Robotics",
      city: "Shanghai",
      status: "Open-source / agile robot line",
      statusTone: "research",
      year: "2025 / 2026 public product line",
      mainUse: "Open-source robot work, smaller humanoid or agile embodied AI development depending on model.",
      mobility: "Humanoid / agile robot platform depending on model",
      keyHardware: "AgiBot lists X1 as a full-stack open-source robot and X2 as a fully intelligent and agile robot.",
      autonomy: "Research, developer ecosystem and controlled tasks; exact autonomy varies by model.",
      sourceLabel: "AgiBot X series official",
      sourceUrl: "https://www.agibot.com/",
      overview:
        "X1 and X2 are included because they connect AgiBot’s hardware work to open-source tooling and embodied AI experimentation.",
      technicalNotes:
        "The company’s own navigation separates X1 and X2 from the A2 family. This page does not merge them into the industrial A2 line.",
      whyItMatters:
        "Open-source bodies and tools can widen China’s developer base for embodied AI, especially when paired with datasets and teleoperation.",
      limitations:
        "Exact form factor, customer status and deployment claims should be checked model by model before reuse."
    },
    {
      id: "xpeng-iron",
      name: "XPeng IRON",
      company: "XPeng",
      city: "Guangzhou",
      status: "Prototype / pilot humanoid",
      statusTone: "prototype",
      year: "2024 first reveal; 2025 / 2026 newer public versions",
      mainUse: "Physical AI, retail, factory and corporate environment pilots linked to XPeng’s EV ecosystem.",
      mobility: "Bipedal humanoid",
      keyHardware: "XPeng has described IRON as part of its physical AI program. Public reports describe humanlike joints and a commercial rollout plan, but exact specs should be source-checked by generation.",
      autonomy: "Prototype and pilot strategy. Not independent mass deployment.",
      sourceLabel: "Reuters XPeng robotics update",
      sourceUrl: "https://www.reuters.com/world/china/xpeng-boss-head-robot-unit-with-humanoid-mass-production-imminent-2026-06-10/",
      overview:
        "IRON is XPeng’s humanoid bet. It matters because it ties robotics to an EV company with batteries, motors, factories, AI chips and retail environments.",
      technicalNotes:
        "IRON should be described through dated sources because XPeng has shown different public iterations. The article labels it as prototype / pilot rather than a mature factory worker.",
      whyItMatters:
        "XPeng can test humanoids in places it already controls showrooms, factories and vehicle supply chains. That gives IRON a plausible pilot path.",
      limitations:
        "A lifelike walk and onstage reveal do not prove uptime, manipulation reliability or return on investment. Public balance failures and staged conditions should remain part of the analysis when relevant."
    },
    {
      id: "leju-kuavo",
      name: "Leju Kuavo",
      company: "Leju Robotics",
      city: "Shenzhen",
      status: "Active full-size platform",
      statusTone: "active",
      year: "2020s",
      mainUse: "General-purpose humanoid research, education and industrial exploration.",
      mobility: "Bipedal humanoid",
      keyHardware: "Leju positions Kuavo as its full-size humanoid series; public detail should be checked per model.",
      autonomy: "Research, demos and industrial exploration. Not claimed as autonomous factory replacement.",
      sourceLabel: "Leju official site",
      sourceUrl: "https://www.lejurobot.com/en",
      overview:
        "Kuavo is Leju’s full-size humanoid line and a key part of the Shenzhen humanoid cluster alongside UBTECH and EngineAI.",
      technicalNotes:
        "Leju’s older portfolio includes small education robots and bipedal platforms, while Kuavo represents the larger humanoid direction.",
      whyItMatters:
        "Leju matters because it has experience with educational humanoids and is moving toward larger industrial bodies.",
      limitations:
        "The article does not quote exact Kuavo specs unless the official product sheet is available."
    },
    {
      id: "leju-aelos-pando",
      name: "Leju AELOS / PANDO",
      company: "Leju Robotics",
      city: "Shenzhen",
      status: "Education / smaller humanoids",
      statusTone: "research",
      year: "2016 onward for AELOS; 2018 for PANDO in public histories",
      mainUse: "Education, research, competitions and smaller humanoid development.",
      mobility: "Small bipedal humanoid robots",
      keyHardware: "Small education-oriented humanoid bodies and servo-based platforms; exact variants differ by product.",
      autonomy: "Education and programmable research use.",
      sourceLabel: "Leju official site",
      sourceUrl: "https://www.lejurobot.com/en",
      overview:
        "AELOS and PANDO show the education side of Leju’s history. They are not the same category as a full-size industrial humanoid.",
      technicalNotes:
        "The page keeps these robots in the research and education class because small humanoids are useful for teaching gait, control and robotics programming.",
      whyItMatters:
        "Educational humanoids build the talent base that later supports larger platforms.",
      limitations:
        "Small education robots should not be used as evidence for industrial humanoid deployment."
    },
    {
      id: "engineai-sa01-se01-pm01",
      name: "EngineAI SA01 / SE01 / PM01",
      company: "EngineAI",
      city: "Shenzhen",
      status: "Active bipedal and humanoid platforms",
      statusTone: "active",
      year: "2024 onward public product visibility",
      mainUse: "Bipedal and humanoid robot platforms for motion, demonstration, research and emerging service scenarios.",
      mobility: "SA01 bipedal robot; SE01 full-size humanoid; PM01 compact humanoid class if confirmed by product material.",
      keyHardware: "Public company profiles identify SA01, SE01 and PM01 in the EngineAI lineup; use official product pages for final dimensions and DoF.",
      autonomy: "Controlled demos and platform development. Not described as proven autonomous labor.",
      sourceLabel: "EngineAI official site",
      sourceUrl: "https://www.engineai.com.cn/",
      overview:
        "EngineAI is part of Shenzhen’s second wave of humanoid startups. Its systems became visible through unusually smooth walking and bipedal motion demonstrations.",
      technicalNotes:
        "SA01 is best treated carefully as a bipedal robot platform. SE01 is the fuller humanoid entry. PM01 is included only as a platform in the public lineup, with exact details left to official sheets.",
      whyItMatters:
        "EngineAI matters because walking quality, balance recovery and motion control remain core bottlenecks for humanoids.",
      limitations:
        "High-motion demos, boxing demos and public stunts do not prove useful work."
    },
    {
      id: "astribot-s1",
      name: "Astribot S1",
      company: "Stardust Intelligence / Astribot",
      city: "Shenzhen",
      status: "Manipulation-focused humanoid / upper-body platform",
      statusTone: "research",
      year: "2024 onward public demos; 2025 / 2026 research visibility",
      mainUse: "Whole-body manipulation, teleoperation, household-style tasks and embodied AI data collection.",
      mobility: "Upper-body / humanoid manipulation system depending on configuration",
      keyHardware: "Public research describes Astribot Suite for whole-body manipulation, teleoperation interface and robot learning. Exact body configuration should be verified per release.",
      autonomy: "Teleoperation and learned policies. Not full autonomy.",
      sourceLabel: "Astribot Suite research",
      sourceUrl: "https://arxiv.org/abs/2507.17141",
      overview:
        "Astribot S1 became known for fast, smooth manipulation videos. The important part is not just speed, but the data and teleoperation pipeline behind the work.",
      technicalNotes:
        "The available research frames Astribot around whole-body manipulation, human demonstrations and policy learning. This page does not treat viral clips as final deployment proof.",
      whyItMatters:
        "Manipulation is where humanoids become useful or fail. Astribot sits directly in that problem.",
      limitations:
        "Public videos can hide resets, selected takes and human control. The page labels Astribot as manipulation-focused research, not as a home robot."
    },
    {
      id: "kepler-forerunner",
      name: "Kepler Forerunner K1 / K2",
      company: "Kepler Exploration Robotics",
      city: "Shanghai",
      status: "Watchlist / active humanoid development",
      statusTone: "watchlist",
      year: "2020s",
      mainUse: "Humanoid robot development for research, service and industrial scenarios depending on model.",
      mobility: "Bipedal humanoid platform",
      keyHardware: "Public product information should be checked directly from Kepler before quoting exact model specifications.",
      autonomy: "Watchlist. No broad deployment claim in this page.",
      sourceLabel: "Kepler official site",
      sourceUrl: "https://www.kpla-robot.com/",
      overview:
        "Kepler is included because it is one of the Shanghai humanoid companies often discussed alongside Fourier and AgiBot.",
      technicalNotes:
        "The article keeps Kepler in a watchlist/active development label and avoids hard specs when official public data is not stable in the current crawl.",
      whyItMatters:
        "Kepler adds to the density of Shanghai’s humanoid cluster.",
      limitations:
        "Model names and exact specs should be verified from current official product sheets before reuse."
    },
    {
      id: "xiaomi-cyberone",
      name: "Xiaomi CyberOne",
      company: "Xiaomi",
      city: "Beijing",
      status: "Historical / prototype",
      statusTone: "historical",
      year: "2022",
      mainUse: "Prototype humanoid demonstration linked to Xiaomi’s robotics and consumer electronics ecosystem.",
      mobility: "Bipedal humanoid prototype",
      keyHardware: "Public Xiaomi material positioned CyberOne as a humanoid concept prototype. This page does not claim current product availability.",
      autonomy: "Prototype demonstration, not commercial humanoid deployment.",
      sourceLabel: "Xiaomi CyberOne official news",
      sourceUrl: "https://blog.mi.com/en/2022/08/11/xiaomi-presents-its-first-humanoid-robot-cyberone/",
      overview:
        "CyberOne matters as a signal that Xiaomi, one of China’s biggest consumer electronics companies, tested humanoid robotics publicly.",
      technicalNotes:
        "CyberOne should be kept separate from Xiaomi’s current robotics software and VLA research work. It was a visible humanoid prototype, not a sold worker.",
      whyItMatters:
        "It connected humanoid robotics to consumer electronics supply chains, cameras, batteries, edge AI and brand-level R&D.",
      limitations:
        "No current large public deployment path is assumed."
    },
    {
      id: "cloudminds-xr-1",
      name: "CloudMinds XR-1",
      company: "CloudMinds",
      city: "China / Beijing-linked operations",
      status: "Service humanoid / legacy status unclear",
      statusTone: "watchlist",
      year: "2019 public visibility",
      mainUse: "Cloud-connected service humanoid, reception and interaction scenarios.",
      mobility: "Humanoid service robot",
      keyHardware: "CloudMinds described cloud-connected robot intelligence and service robot systems; current product status should be verified before reuse.",
      autonomy: "Cloud-assisted service robotics, not independent general autonomy.",
      sourceLabel: "CloudMinds official site",
      sourceUrl: "https://www.cloudminds.com/",
      overview:
        "XR-1 belongs to an earlier wave of cloud-connected humanoid service robots. It is included for context, but its current commercial status is not treated as active without confirmation.",
      technicalNotes:
        "The page uses a careful legacy/watchlist label because CloudMinds’ wider work includes cloud robot infrastructure and service robotics, not only a single humanoid body.",
      whyItMatters:
        "XR-1 shows that China’s humanoid ecosystem had service and cloud-robot concepts before the 2023-2026 bipedal wave.",
      limitations:
        "Current availability, fleet status and deployment details are not assumed."
    },
    {
      id: "tiangong",
      name: "Tiangong / TianGong",
      company: "Beijing Humanoid Robot Innovation Center",
      city: "Beijing",
      status: "Public research platform",
      statusTone: "research",
      year: "2024 / 2025 public visibility",
      mainUse: "Open research platform, public robotics programs and endurance demonstrations.",
      mobility: "Bipedal humanoid",
      keyHardware: "Public reporting links Tiangong Ultra to Beijing’s humanoid innovation center and half-marathon demonstrations. Exact specs should be sourced per model.",
      autonomy: "Research and event demonstrations; not generalized deployment.",
      sourceLabel: "Reuters / Beijing humanoid coverage",
      sourceUrl: "https://www.reuters.com/technology/chinas-humanoid-robots-will-not-replace-human-workers-beijing-official-says-2025-05-17/",
      overview:
        "Tiangong is important because it connects Beijing’s public robotics strategy to humanoid platforms and open ecosystem work.",
      technicalNotes:
        "Humanoid races and endurance events reveal both progress and limitations battery swaps, falls, resets and human support remain important details.",
      whyItMatters:
        "It shows how China uses public platforms and events to push robotics visibility beyond individual startups.",
      limitations:
        "Race completion does not equal useful industrial deployment."
    },
    {
      id: "booster-t1-k1",
      name: "Booster K1 / T1 / T2",
      company: "Booster Robotics",
      city: "China",
      status: "Confirmed education and research humanoid platforms",
      statusTone: "research",
      year: "2024–2026 product family",
      mainUse: "Research, education, robotics competitions and embodied development.",
      mobility: "Bipedal humanoid platforms",
      keyHardware: "Booster lists K1 at about 95 cm, 19.5 kg and 22 DoF. T1 is about 118 cm, 30 kg and 23 DoF. T2 is about 140 cm with 31 DoF.",
      autonomy: "Developer platforms; task autonomy depends on the software and configuration.",
      sourceLabel: "Booster official site",
      sourceUrl: "https://www.booster.tech/",
      overview:
        "Booster has current official product pages for K1, T1 and T2. The company positions them as bipedal developer platforms for education, competitions, reinforcement-learning work and application development.",
      technicalNotes:
        "The official pages separate the smaller K1 and T1 from the larger T2. Their dimensions, mass and joint counts should be compared by configuration rather than combined into one Booster specification.",
      whyItMatters:
        "K1, T1 and T2 give developers three body sizes with published joint counts and current product documentation.",
      limitations:
        "Competition results and product specifications do not establish factory uptime or autonomous task performance."
    },
    {
      id: "paxini-tora",
      name: "PaXini TORA",
      company: "PaXini Technology",
      city: "China",
      status: "Watchlist",
      statusTone: "watchlist",
      year: "2020s",
      mainUse: "Humanoid-related robotics and tactile intelligence watchlist.",
      mobility: "Humanoid status should be confirmed by official material before full classification.",
      keyHardware: "Do not quote hands, tactile sensors or full-body specs without the current official page.",
      autonomy: "Watchlist only.",
      sourceLabel: "PaXini official site",
      sourceUrl: "https://www.paxini.com/",
      overview:
        "PaXini appears in humanoid and tactile robotics discussions, but this page keeps TORA in watchlist status because product status and humanoid classification need current official confirmation.",
      technicalNotes:
        "The page intentionally avoids specs and autonomy claims here.",
      whyItMatters:
        "PaXini is relevant to track because tactile sensing and dexterous manipulation still require matched task, contact and sensor data for useful comparison.",
      limitations:
        "Watchlist means not treated as a confirmed deployed humanoid product."
    },
    {
      id: "galbot",
      name: "Galbot G1 / G1 Lite",
      company: "Galbot",
      city: "China",
      status: "Documented wheeled robot platforms",
      statusTone: "active",
      year: "Developer documentation checked October 2026",
      mainUse: "Mobile manipulation, greeting and application development, with software-package availability varying by task.",
      mobility: "Wheeled platform",
      keyHardware: "The developer documentation identifies G1 and G1 Lite as wheeled robots with APIs for sensing, planning, control, navigation and interaction.",
      autonomy: "Task behaviour depends on the configuration and software package; no general autonomy claim is made here.",
      sourceLabel: "Galbot developer product documentation",
      sourceUrl: "https://developer.galbot.com/",
      overview:
        "Galbot documents G1 and G1 Lite as wheeled robot platforms. Their mobility category should remain separate from bipedal robots.",
      technicalNotes:
        "On 11 October 2026, the developer page listed the greeting package as available, the sorting package as forthcoming and the inspection package as planned. A hardware listing does not establish that every application package is released.",
      whyItMatters:
        "The documentation separates hardware, development tools and task packages. A buyer can check the package needed for the intended workflow.",
      limitations:
        "The cited documentation does not provide an independent task-success rate or verify a specific customer deployment."},
    {
      id: "limx-tron",
      name: "LimX Dynamics TRON",
      company: "LimX Dynamics",
      city: "Shenzhen",
      status: "Bipedal / legged platform watchlist",
      statusTone: "watchlist",
      year: "2020s",
      mainUse: "Legged locomotion research and robot mobility platform.",
      mobility: "Bipedal / legged platform; not listed as full humanoid unless upper-body configuration is official.",
      keyHardware: "Legged platform details should be taken from LimX official sources. This page does not mix TRON into the main humanoid worker category.",
      autonomy: "Watchlist only for humanoid relation.",
      sourceLabel: "LimX official site",
      sourceUrl: "https://www.limxdynamics.com/",
      overview:
        "TRON is tracked because humanoid development often starts with legs, balance and locomotion before full hands and upper-body manipulation arrive.",
      technicalNotes:
        "The page keeps TRON as bipedal/legged unless an official full humanoid upper body is confirmed.",
      whyItMatters:
        "Locomotion platforms influence humanoid control stacks and reinforcement learning work.",
      limitations:
        "Not counted as a confirmed full humanoid robot in the main classification."
    },
    {
      id: "xianxingzhe",
      name: "Xianxingzhe",
      company: "National University of Defense Technology",
      city: "Changsha",
      status: "Historical bipedal humanoid",
      statusTone: "historical",
      year: "2000",
      mainUse: "Early Chinese bipedal humanoid research.",
      mobility: "Bipedal humanoid research platform",
      keyHardware: "Historical platform; current detailed specifications are not repeated without a reliable archival source.",
      autonomy: "Historical research platform.",
      sourceLabel: "Historical reference",
      sourceUrl: "https://en.wikipedia.org/wiki/Humanoid_robot",
      overview:
        "Xianxingzhe is included to avoid making China’s humanoid story look as if it began in 2023. It belongs to an earlier university and defense-research period of bipedal humanoid work.",
      technicalNotes:
        "This page keeps the entry historical and does not add modern performance claims.",
      whyItMatters:
        "It gives the timeline a deeper base before the current commercial rush.",
      limitations:
        "The page does not treat Xianxingzhe as a current product."
    }
  ],
  companies: [
    { name: "Unitree Robotics", founded: "2016", headquarters: "Hangzhou", mainRobot: "H1, G1, R1", useCase: "Cost-focused humanoid and quadruped platforms", status: "Active", sourceUrl: "https://www.unitree.com/", sourceLabel: "Unitree" },
    { name: "UBTECH Robotics", founded: "2012", headquarters: "Shenzhen", mainRobot: "Walker S / S2", useCase: "Industrial, service and companion humanoids", status: "Public company / active", sourceUrl: "https://www.ubtrobot.com/", sourceLabel: "UBTECH" },
    { name: "Fourier", founded: "2015", headquarters: "Shanghai", mainRobot: "GR-1, GR-2, GR-3", useCase: "Rehabilitation robotics and humanoid platforms", status: "Active", sourceUrl: "https://www.fftai.com/", sourceLabel: "Fourier" },
    { name: "AgiBot / Zhiyuan Robotics", founded: "2023", headquarters: "Shanghai", mainRobot: "A2, A2-W, X1, X2", useCase: "Humanoids, embodied AI data and industrial pilots", status: "Active", sourceUrl: "https://www.agibot.com/", sourceLabel: "AgiBot" },
    { name: "XPeng", founded: "2014", headquarters: "Guangzhou", mainRobot: "IRON", useCase: "Physical AI, EV ecosystem and pilot humanoid work", status: "Active robotics division", sourceUrl: "https://www.xpeng.com/", sourceLabel: "XPeng" },
    { name: "Leju Robotics", founded: "2016", headquarters: "Shenzhen", mainRobot: "Kuavo, AELOS, PANDO", useCase: "Education, research and full-size humanoids", status: "Active", sourceUrl: "https://www.lejurobot.com/en", sourceLabel: "Leju" },
    { name: "EngineAI", founded: "2023", headquarters: "Shenzhen", mainRobot: "SA01, SE01, PM01", useCase: "Bipedal and humanoid robot platforms", status: "Active", sourceUrl: "https://www.engineai.com.cn/", sourceLabel: "EngineAI" },
    { name: "Astribot / Stardust Intelligence", founded: "Not publicly confirmed here", headquarters: "Shenzhen", mainRobot: "Astribot S1", useCase: "Manipulation-focused humanoid work", status: "Active research / product watch", sourceUrl: "https://www.astribot.com/", sourceLabel: "Astribot" },
    { name: "Kepler", founded: "Not publicly confirmed here", headquarters: "Shanghai", mainRobot: "Forerunner", useCase: "Humanoid robot development", status: "Watchlist / active development", sourceUrl: "https://www.kpla-robot.com/", sourceLabel: "Kepler" },
    { name: "Xiaomi", founded: "2010", headquarters: "Beijing", mainRobot: "CyberOne", useCase: "Prototype humanoid and robotics software research", status: "Historical humanoid prototype", sourceUrl: "https://blog.mi.com/en/2022/08/11/xiaomi-presents-its-first-humanoid-robot-cyberone/", sourceLabel: "Xiaomi" },
    { name: "CloudMinds", founded: "2015", headquarters: "China / global operations", mainRobot: "XR-1", useCase: "Cloud-connected service humanoids", status: "Legacy / watchlist", sourceUrl: "https://www.cloudminds.com/", sourceLabel: "CloudMinds" },
    { name: "Beijing Humanoid Robot Innovation Center", founded: "Public center", headquarters: "Beijing", mainRobot: "Tiangong", useCase: "Public research and open ecosystem", status: "Active research", sourceUrl: "https://www.reuters.com/technology/chinas-humanoid-robots-will-not-replace-human-workers-beijing-official-says-2025-05-17/", sourceLabel: "Reuters" }
  ],
  hubs: [
    { city: "Hangzhou", detail: "Unitree gives the city one of China’s highest-visibility humanoid and quadruped makers.", robots: ["Unitree H1", "Unitree G1", "Unitree R1"] },
    { city: "Shenzhen", detail: "The Greater Bay Area cluster includes UBTECH, Leju, EngineAI, LimX and the Astribot ecosystem.", robots: ["Walker S", "Kuavo", "SE01", "Astribot S1", "LimX TRON"] },
    { city: "Shanghai", detail: "Shanghai combines Fourier, AgiBot and Kepler with strong hardware and AI startup density.", robots: ["GR-1", "GR-2", "AgiBot A2", "Kepler Forerunner"] },
    { city: "Guangzhou", detail: "XPeng links humanoids to EV manufacturing, physical AI and retail environments.", robots: ["XPeng IRON"] },
    { city: "Beijing", detail: "Beijing connects national robotics programs, Xiaomi’s CyberOne prototype and Tiangong public research.", robots: ["CyberOne", "Tiangong"] },
    { city: "Changsha", detail: "Changsha adds historical depth through National University of Defense Technology humanoid work.", robots: ["Xianxingzhe"] },
    { city: "Dongguan / Greater Bay Area", detail: "Manufacturing suppliers, motors, batteries, electronics and contract manufacturing support fast robot iteration.", robots: ["Supply chain"] }
  ],
  timeline: [
    { label: "2000", detail: "Xianxingzhe becomes one of China’s early bipedal humanoid robots in public historical accounts.", sourceUrl: "https://en.wikipedia.org/wiki/Humanoid_robot" },
    { label: "2012", detail: "UBTECH is founded in Shenzhen and later builds the Walker humanoid family.", sourceUrl: "https://www.ubtrobot.com/" },
    { label: "2015", detail: "Fourier is founded in Shanghai with a rehabilitation robotics base before moving into humanoids.", sourceUrl: "https://www.fftai.com/" },
    { label: "2016", detail: "Unitree and Leju become important Chinese robotics companies in legged and humanoid-related systems.", sourceUrl: "https://www.unitree.com/" },
    { label: "2022", detail: "Xiaomi reveals CyberOne, a public humanoid prototype from a consumer electronics giant.", sourceUrl: "https://blog.mi.com/en/2022/08/11/xiaomi-presents-its-first-humanoid-robot-cyberone/" },
    { label: "2023", detail: "Unitree H1 and Fourier GR-1 push Chinese full-size humanoid visibility.", sourceUrl: "https://www.unitree.com/h1/" },
    { label: "2023", detail: "AgiBot / Zhiyuan Robotics is founded in Shanghai and later links humanoids to embodied AI data infrastructure.", sourceUrl: "https://www.agibot.com/" },
    { label: "2024", detail: "Unitree G1 lowers the entry price for humanoid robot platforms and becomes widely used in research discussions.", sourceUrl: "https://www.unitree.com/g1/" },
    { label: "2024", detail: "UBTECH Walker S series gains more attention in industrial pilot scenarios.", sourceUrl: "https://www.ubtrobot.com/en/humanoid/products/walker-s1" },
    { label: "2024", detail: "XPeng introduces IRON as part of its physical AI strategy.", sourceUrl: "https://www.reuters.com/world/china/xpeng-boss-head-robot-unit-with-humanoid-mass-production-imminent-2026-06-10/" },
    { label: "2025", detail: "Chinese humanoid companies accelerate demos, factory pilots and embodied AI data collection.", sourceUrl: "https://www.agibot.com/" },
    { label: "2026", detail: "Booster publishes T2 as a 31-DoF flagship development platform alongside its K1 and T1 product lines.", sourceUrl: "https://www.booster.tech/booster-t2/" }
  ],
  classifications: [
    {
      "label": "Active or recent humanoid platforms",
      "robots": [
        "Unitree H1",
        "Unitree G1",
        "Unitree R1",
        "UBTECH Walker S / S1 / S2",
        "Fourier GR-1 / GR-2",
        "AgiBot A2 / A3 / X2",
        "XPeng IRON",
        "Leju Kuavo",
        "EngineAI SE01",
        "Astribot S1",
        "Kepler Forerunner"
      ]
    },
    {
      "label": "Service, companion or interaction-focused",
      "robots": [
        "UBTECH UWorld U1",
        "CloudMinds XR-1",
        "Xiaomi CyberOne",
        "UBTECH Walker X"
      ]
    },
    {
      "label": "Research, education or smaller platforms",
      "robots": [
        "Leju AELOS",
        "Leju PANDO",
        "EngineAI SA01",
        "Tiangong",
        "Booster K1 / T1 / T2"
      ]
    },
    {
      "label": "Historical platforms",
      "robots": [
        "Xianxingzhe",
        "Xiaomi CyberOne if no current development path is confirmed"
      ]
    },
    {
      "label": "Wheeled mobile manipulation",
      "robots": [
        "Galbot G1 / G1 Lite"
      ]
    },
    {
      "label": "Watchlist only",
      "robots": [
        "PaXini TORA",
        "LimX TRON",
        "Any robot seen only in a viral demo without a verified product page"
      ]
    }
  ],
  whyChinaMatters: [
    "China has dense hardware supply chains around motors, reducers, batteries, cameras, structural parts and electronics. That makes fast iteration easier than in regions where every prototype requires long sourcing cycles.",
    "The ecosystem is distributed across real industrial cities. Hangzhou has Unitree, Shenzhen has UBTECH, Leju and EngineAI, Shanghai has Fourier and AgiBot, Guangzhou has XPeng and Beijing has public humanoid research platforms.",
    "Chinese humanoid companies are not only building bodies. AgiBot is building embodied AI datasets, Astribot focuses on whole-body manipulation data and Unitree platforms appear in academic work on teleoperation, fall recovery and whole-body motion control.",
    "Factory partners are willing to test humanoids, especially in automotive, electronics, logistics and public demonstrations. That gives companies data from physical environments, but pilots must still be separated from repeatable daily deployment.",
    "Lower-cost platforms such as Unitree G1 matter because they put humanoid hardware in front of more developers and research teams. More bodies create more software experiments, more failures and more practical lessons."
  ],
  limitations: [
    "Many robots are still in controlled demos, pilot programs or staged videos.",
    "Factory pilots do not automatically mean repeatable daily deployment or positive ROI.",
    "Humanoid robots still struggle with dexterous manipulation, tool use, battery life, safety, reliability and maintenance.",
    "Teleoperation and imitation learning are useful, but they are not the same as independent autonomy.",
    "Public videos often hide resets, human operators, limited task scope and failed attempts.",
    "Low hardware price does not automatically mean low operating cost.",
    "Walking, dancing and boxing demos do not prove factory productivity.",
    "Industrial deployment needs uptime, spare parts, safety certification, integration with workflows and clear task economics."
  ],
  regionComparison: [
    { region: "China", detail: "Fast hardware iteration, lower-cost platforms and a rising number of factory pilot activities." },
    { region: "United States", detail: "Strong AI labs, venture-backed humanoid startups and warehouse/logistics pilot activity." },
    { region: "Japan", detail: "Deep historical research in bipedal walking, androids, humanoid interaction and research platforms." },
    { region: "Europe", detail: "Strong research labs, industrial safety culture and specialized humanoid companies." }
  ],
  relatedLinks: [
    { label: "Humanoid robots main page", path: "/robot-types/humanoid-robots" },
    { label: "French humanoid robots", path: "/humanoid-robots/france" },
    { label: "Japanese humanoid robots", path: "/humanoid-robots/japan" },
    { label: "Humanoid robot companies", path: "/robotics-companies" },
    { label: "Robot data collection", path: "/robotics-data-collection" },
    { label: "Robotics software", path: "/robotics-software" },
    { label: "Robot components", path: "/robot-components" },
    { label: "Physical AI topics", path: "/robotics-topics" }
  ]
};
